import { useAnalytics } from 'hooks/useAnalytics';
import { CookiebotDeclined, useLocalStorage } from 'hooks/userPreferences';
import { COOKIEBOT_ENABLED } from 'lib/cookiebot';
import { useEffect, useRef, useState } from 'react';
import { isBrowser } from 'utils/environment';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

interface CookieConsent {
  hasResponse: boolean;
  consented: boolean;
  necessary: boolean;
  preferences: boolean;
  statistics: boolean;
  marketing: boolean;
  method: 'implied' | 'explicit' | null;
}

interface ConsentStore {
  consent: CookieConsent;
  setConsent: (consent: CookieConsent) => void;
}

const useConsentStore = create<ConsentStore>((set) => ({
  consent: {
    hasResponse: false,
    consented: false,
    necessary: false,
    preferences: false,
    statistics: false,
    marketing: false,
    method: null,
  },
  setConsent: (consent) => set({ consent }),
}));

/**
 * Whether the user has responded to the cookie banner (accepted or declined).
 *
 * Reads the shared consent store directly, so it does NOT register Cookiebot
 * event listeners - safe to call anywhere without duplicating the analytics
 * events / reload wired up by the single `useCookiebotConsent` mount in `_app`.
 */
export const useHasCookieConsentResponse = () =>
  useConsentStore((state) => state.consent.hasResponse);

export const useIsCookieConsented = () =>
  useConsentStore((state) => state.consent.consented);

export const useCookiebotConsent = () => {
  const setConsent = useConsentStore((state) => state.setConsent);
  const consent = useConsentStore(useShallow((state) => state.consent));
  const [isLoaded, setIsLoaded] = useState(false);
  const { analytics } = useAnalytics();
  const dialogShownThisVisitRef = useRef(false);
  const acceptFiredThisVisitRef = useRef(false);
  const declineFiredThisVisitRef = useRef(false);
  const hadConsentRef = useRef(false);

  const {
    value: declinedTimestamp,
    setStoredValue: setDeclinedTimestamp,
    removeStoredValue: removeDeclinedTimestamp,
  } = useLocalStorage(CookiebotDeclined);

  useEffect(() => {
    if (!isBrowser) return;

    if (!COOKIEBOT_ENABLED) {
      setConsent({
        hasResponse: true,
        consented: false,
        necessary: false,
        preferences: false,
        statistics: false,
        marketing: false,
        method: null,
      });
      setIsLoaded(true);
      return;
    }

    const updateConsent = () => {
      if (!window.Cookiebot?.hasResponse) return;
      if (window.Cookiebot.consented) hadConsentRef.current = true;
      setConsent({
        hasResponse: window.Cookiebot.hasResponse,
        consented: window.Cookiebot.consented,
        necessary: window.Cookiebot.consent.necessary,
        preferences: window.Cookiebot.consent.preferences,
        statistics: window.Cookiebot.consent.statistics,
        marketing: window.Cookiebot.consent.marketing,
        method: window.Cookiebot.consent.method,
      });
    };

    // Fires when the user clicks accept as well as when the user returns after accepting
    const handleUserHasAccepted = () => {
      if (dialogShownThisVisitRef.current && !acceptFiredThisVisitRef.current) {
        analytics.cookiesAccept();
        acceptFiredThisVisitRef.current = true;
      }
      removeDeclinedTimestamp();
      updateConsent();
    };

    const handleOnDialogDisplay = () => {
      if (dialogShownThisVisitRef.current) return;
      dialogShownThisVisitRef.current = true;
      analytics.cookiesDisplay();
    };

    // Fires when the user clicks decline as well as when the user returns after declining
    const handleUserHasDeclined = () => {
      if (
        dialogShownThisVisitRef.current &&
        !declineFiredThisVisitRef.current
      ) {
        declineFiredThisVisitRef.current = true;
        analytics.cookiesDecline();
      }
      updateConsent();
      // Reload only when consent was declined, to tear down the scripts that loaded.
      if (hadConsentRef.current && !declinedTimestamp) {
        setDeclinedTimestamp(Date.now().toString());
        window.location.reload();
      }
    };

    // Fires once Cookiebot has loaded and the consent state is known - including
    // for returning visitors whose prior response is restored without a click.
    // Sync the store here so `consent` (and `hasResponse`) is populated on every
    // load, not just via the accept/decline click handlers. Without this, a
    // returning decliner never re-runs `updateConsent` (the decline handler only
    // does so on the first decline, before reloading), leaving `hasResponse`
    // stuck false.
    const handleUserHasAnswered = () => {
      updateConsent();
      setIsLoaded(true);
    };

    updateConsent();
    window.addEventListener('CookiebotOnAccept', handleUserHasAccepted);
    window.addEventListener('CookiebotOnDecline', handleUserHasDeclined);
    window.addEventListener('CookiebotOnLoad', handleUserHasAnswered);
    window.addEventListener('CookiebotOnDialogDisplay', handleOnDialogDisplay);

    return () => {
      window.removeEventListener('CookiebotOnAccept', handleUserHasAccepted);
      window.removeEventListener('CookiebotOnDecline', handleUserHasDeclined);
      window.removeEventListener('CookiebotOnLoad', handleUserHasAnswered);
      window.removeEventListener(
        'CookiebotOnDialogDisplay',
        handleOnDialogDisplay,
      );
    };
  }, [
    setConsent,
    analytics,
    declinedTimestamp,
    removeDeclinedTimestamp,
    setDeclinedTimestamp,
  ]);

  return {
    consent,
    isLoaded,
    isConsented: consent.consented,
  };
};
